// ═══════════════════════════════════════════════════════════════
// F-083 — "validate rule that can never fire" predicate
//
// A field-scope `validate` RuleAttachment only does work at runtime when:
//   1. it resolves a target field — rule-runtime's iterAuthoredFieldValidates
//      (content-validate.ts) computes `src.targetField ?? rule.fieldRef` and
//      SILENTLY SKIPS the source when neither is set; and
//   2. it actually expresses a check — a condition (the drawer's F-020
//      requirement) OR a parameterized validationType config (a
//      condition-less pattern/compare/oneOf/… binding fires fine through
//      collectValidateBindings, so the server gate is deliberately more
//      permissive than the drawer here).
//
// This module is the SINGLE predicate shared by the publish gate
// (validators.ts → `rule.validate-cannot-fire`), the draft-write API guard
// (job-type-versions PATCH), and the rules-list "never fires" warning chip —
// so the three surfaces can't drift. The source fan-out and the server-path
// type set come from validate-rule-sources.ts, shared WITH the runtime
// collector itself, so the predicate can't drift from what it predicts.
// ═══════════════════════════════════════════════════════════════
import { SERVER_EVALUATED_VALIDATE_TYPES, validateRuleSources, } from './validate-rule-sources.js';
function hasNumericParam(v) {
    if (v === undefined || v === null || v === '')
        return false;
    return !Number.isNaN(typeof v === 'number' ? v : Number(v));
}
/**
 * True when the config expresses a check that can actually fail a value.
 * Vocabulary mirrors normalizeValidateConfig: the editor `validationType`
 * wins over the canonical/legacy `type`. An UNKNOWN type returns true —
 * normalizeValidateConfig preserves it verbatim and the engine fails it
 * CLOSED (F-017), i.e. it fires (loudly), so it is not a silent no-op.
 */
function configSpecifiesCheck(config) {
    const raw = typeof config['validationType'] === 'string'
        ? config['validationType']
        : typeof config['type'] === 'string' ? config['type'] : undefined;
    if (!raw)
        return false;
    if (SERVER_EVALUATED_VALIDATE_TYPES.has(raw))
        return true; // fires on the server path
    switch (raw) {
        case 'required':
            return true;
        case 'regex':
        case 'pattern':
            // Empty pattern → new RegExp('') matches everything → never fails.
            return typeof config['pattern'] === 'string' && config['pattern'].length > 0;
        case 'min':
        case 'max':
        case 'compare':
        case 'minLength':
        case 'maxLength':
            return hasNumericParam(config['value']);
        case 'length':
            return hasNumericParam(config['min']) || hasNumericParam(config['max']);
        case 'oneOf':
            return Array.isArray(config['values']) && config['values'].length > 0;
        case 'custom':
            return typeof config['expression'] === 'string' && config['expression'].trim().length > 0;
        default:
            return true; // unknown → engine fails closed; the rule fires
    }
}
/**
 * Judge a rule against the F-083 "can never fire" predicate.
 *
 * Returns `null` for anything that is NOT a field-scope `validate` rule —
 * other types/scopes have their own execution paths and must never be
 * rejected by this check. Callers decide whether to gate on `isActive`
 * (publish gate and write guard check active rules only; deactivating a
 * broken rule is the deliberate escape hatch).
 */
export function validateRuleCannotFire(rule) {
    if (rule.type !== 'validate' || rule.scope !== 'field')
        return null;
    const sources = validateRuleSources(rule);
    if (!sources.some((s) => s.targetField ?? rule.fieldRef)) {
        return {
            reason: 'no-target-field',
            message: `Rule "${rule.name}" has no target field (fieldRef or action targetField) — the runtime silently skips it`,
        };
    }
    const hasCondition = (rule.condition?.conditions?.length ?? 0) > 0;
    if (hasCondition)
        return null;
    if (sources.some((s) => configSpecifiesCheck(s.config)))
        return null;
    return {
        reason: 'no-condition-or-check',
        message: `Rule "${rule.name}" has no condition and no configured check — it validates nothing`,
    };
}
//# sourceMappingURL=validate-rule-cannot-fire.js.map